import * as React from "react";
import {
  SingleNavMenuLink,
  toLinkButton,
  toLinkDivider,
  toLinkExternal,
  toLinkPage,
  toLinkSpace,
} from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValue, useCpFormModifiedAnyId, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Mk8sCluster } from "../../schema/types/mk8s/entity/mk8s";
import { Mk8sDetailHeader } from "../workload/mk8sHeader";
import { Summary } from "./summary";
import { mk8sHelpers } from "../../mst/kinds/mk8s.helpers";
import { Provider } from "./create/provider/provider";
import { Addons } from "./create/addons/addons";
import { Firewall } from "./create/firewall";
import { KubeConfig } from "./kubeConfig";
import { Mk8sLogs } from "./logs";
import { UnmanagedNodePools } from "./create/provider/unmanagedNodePools";
import { NodePools } from "./create/provider/nodePools";
import { AddonByok } from "./create/addons/byok";
import { AddonAwsEcr } from "./create/addons/awsEcr";
import { AddonAwsEfs } from "./create/addons/awsEfs";
import { AddonAwsElb } from "./create/addons/awsElb";
import { AddonAzureAcr } from "./create/addons/azureAcr";
import { AddonAzureWorkloadIdentity } from "./create/addons/azureWorkloadIdentity";
import { AddonLogs } from "./create/addons/logs";
import { AddonMetrics } from "./create/addons/metrics";
import { AddonNvidia } from "./create/addons/nvidia";
import { AddonRegistryMirror } from "./create/addons/registryMirror";
import { CpRootError } from "../gvc/testNewForm/CpRootError";
import { Mk8sNodes } from "./nodes/Mk8sNodes";

interface Props {
  mk8s: Mk8sCluster;
  refetch?: () => void;
}
export const Mk8sDetailContent: React.FC<Props> = ({ mk8s, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/mk8s/:mk8s/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(mk8s).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(mk8s.lastModified);
  }, [mk8s.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValue(`meta.submitting`);
  const asyncValidating = useCpFormAnyValue(`meta.asyncValidating`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const isAddonCplnPlatformEnabled = useCpFormValue(`addons.byok.isEnabled`);
  const isAddonAWSECREnabled = useCpFormValue(`addons.awsECR.isEnabled`);
  const isAddonAWSEFSEnabled = useCpFormValue(`addons.awsEFS.isEnabled`);
  const isAddonAWSELBEnabled = useCpFormValue(`addons.awsELB.isEnabled`);
  const isAddonACREnabled = useCpFormValue(`addons.azureACR.isEnabled`);
  const isAddonAWIEnabled = useCpFormValue(`addons.azureWorkloadIdentity.isEnabled`);
  const isAddonLogsEnabled = useCpFormValue(`addons.logs.isEnabled`);
  const isAddonMetricsEnabled = useCpFormValue(`addons.metrics.isEnabled`);
  const isAddonNvidiaEnabled = useCpFormValue(`addons.nvidia.isEnabled`);
  const isAddonRegistryMirrorEnabled = useCpFormValue(`addons.registryMirror.isEnabled`);

  const provider = mk8sHelpers.getProvider(mk8s);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];

    let providerNames: string[] = [];
    switch (provider) {
      case "aws":
        providerNames = [
          `aws.autoscaler`,
          `aws.awsTags`,
          `aws.deployRoleArn`,
          `aws.deployRoleChain`,
          `aws.diskEncryptionKeyArn`,
          `aws.extraNodePolicies`,
          `aws.image`,
          `aws.keyPair`,
          `aws.networking`,
          `aws.preInstallScript`,
          `aws.region`,
          `aws.securityGroupIds`,
          `aws.skipCreateRoles`,
          `aws.vpcId`,
        ];
        break;
      case "azure":
        providerNames = [
          `azure.location`,
          `azure.subscriptionId`,
          `azure.sdkSecretLink`,
          `azure.resourceGroup`,
          `azure.networking`,
          `azure.preInstallScript`,
          `azure.image`,
          `azure.sshKeys`,
          `azure.networkId`,
          `azure.tags`,
          `azure.autoscaler`,
        ];
        break;
      case "digitalocean":
        providerNames = [
          `digitalocean.region`,
          `digitalocean.digitalOceanTags`,
          `digitalocean.tokenSecretLink`,
          `digitalocean.vpcId`,
          `digitalocean.image`,
          `digitalocean.sshKeys`,
          `digitalocean.extraSshKeys`,
          `digitalocean.reservedIPs`,
          `digitalocean.autoscaler`,
          `digitalocean.networking`,
          `digitalocean.preInstallScript`,
        ];
        break;
      case "ephemeral":
        providerNames = [`ephemeral.location`];
        break;
      case "gcp":
        providerNames = [
          `gcp.projectId`,
          `gcp.region`,
          `gcp.labels`,
          `gcp.tags`,
          `gcp.metadata`,
          `gcp.network`,
          `gcp.saKeyLink`,
          `gcp.image`,
          `gcp.preInstallScript`,
          `gcp.autoscaler`,
          `gcp.networking`,
        ];
        break;
      case "generic":
        providerNames = [`generic.location`, `generic.networking`];
        break;
      case "hetzner":
        providerNames = [
          `hetzner.region`,
          `hetzner.hetznerLabels`,
          `hetzner.networking`,
          `hetzner.preInstallScript`,
          `hetzner.tokenSecretLink`,
          `hetzner.networkId`,
          `hetzner.firewallId`,
          `hetzner.image`,
          `hetzner.sshKey`,
          `hetzner.autoscaler`,
          `hetzner.floatingIPSelector`,
        ];
        break;
      case "lambdalabs":
        providerNames = [
          `lambdalabs.region`,
          `lambdalabs.tokenSecretLink`,
          `lambdalabs.sshKey`,
          `lambdalabs.preInstallScript`,
          `lambdalabs.autoscaler`,
          `lambdalabs.fileSystems`,
        ];
        break;
      case "linode":
        providerNames = [
          `linode.region`,
          `linode.tokenSecretLink`,
          `linode.firewallId`,
          `linode.image`,
          `linode.authorizedUsers`,
          `linode.vpcId`,
          `linode.authorizedKeys`,
          `linode.preInstallScript`,
          `linode.networking`,
          `linode.autoscaler`,
        ];
        break;
      case "oblivus":
        providerNames = [
          `oblivus.datacenter`,
          `oblivus.tokenSecretLink`,
          `oblivus.sshKeys`,
          `oblivus.autoscaler`,
          `oblivus.preInstallScript`,
        ];
        break;
      case "paperspace":
        providerNames = [
          `paperspace.region`,
          `paperspace.tokenSecretLink`,
          `paperspace.sharedDrives`,
          `paperspace.autoscaler`,
          `paperspace.preInstallScript`,
          `paperspace.userIds`,
          `paperspace.networkId`,
        ];
        break;
      case "triton":
        providerNames = [
          `triton.connection`,
          `triton.location`,
          `triton.privateNetworkId`,
          `triton.firewallEnabled`,
          `triton.loadBalancer`,
          `triton.imageId`,
          `triton.sshKeys`,
          `triton.autoscaler`,
          `triton.networking`,
          `triton.preInstallScript`,
        ];
        break;
      default:
        break;
    }

    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Provider", `/provider-${provider}`, {
        names: [...providerNames, `provider-${provider}`],
        showDirty: true,
      }),
      toLinkPage("Node Pools", `/node-pools-${provider}`, { names: [`${provider}.nodePools`], showDirty: true }),
    );
    if (["hetzner"].includes(provider)) {
      links.push(
        toLinkPage("Dedicated Node Pools", `/dedicated-server-node-pools-${provider}`, {
          names: [`${provider}.dedicatedServerNodePools`],
        }),
      );
    }
    if (["lambdalabs", "oblivus", "paperspace"].includes(provider)) {
      links.push(
        toLinkPage("Unmanaged Node Pools", `/unmanaged-node-pools-${provider}`, {
          names: [`${provider}.unmanagedNodePools`],
        }),
      );
    }

    links.push(toLinkPage("Add-Ons", `/addons`, { names: ["addons"] }));
    if (isAddonCplnPlatformEnabled) {
      links.push(toLinkPage("CPLN Platform", `/addons/addon-cpln-platform`, { names: ["addons.byok"], subLevel: 1 }));
    }
    if (isAddonAWSECREnabled) {
      links.push(toLinkPage("AWS ECR", `/addons/addon-aws-ecr`, { names: ["addons.awsECR"], subLevel: 1 }));
    }
    if (isAddonAWSEFSEnabled) {
      links.push(toLinkPage("AWS EFS", `/addons/addon-aws-efs`, { names: ["addons.awsEFS"], subLevel: 1 }));
    }
    if (isAddonAWSELBEnabled) {
      links.push(toLinkPage("AWS ELB", `/addons/addon-aws-elb`, { names: ["addons.awsELB"], subLevel: 1 }));
    }
    if (isAddonACREnabled) {
      links.push(toLinkPage("Azure ACR", `/addons/addon-azure-acr`, { names: ["addons.azureACR"], subLevel: 1 }));
    }
    if (isAddonAWIEnabled) {
      links.push(
        toLinkPage("Azure Workload ID", `/addons/addon-azure-workload-identity`, {
          names: ["addons.azureWorkloadIdentity"],
          subLevel: 1,
        }),
      );
    }
    if (isAddonLogsEnabled) {
      links.push(toLinkPage("Logs", `/addons/addon-logs`, { names: ["addons.logs"], subLevel: 1 }));
    }
    if (isAddonMetricsEnabled) {
      links.push(toLinkPage("Metrics", `/addons/addon-metrics`, { names: ["addons.metrics"], subLevel: 1 }));
    }
    if (isAddonNvidiaEnabled) {
      links.push(toLinkPage("NVIDIA", `/addons/addon-nvidia`, { names: ["addons.nvidia"], subLevel: 1 }));
    }
    if (isAddonRegistryMirrorEnabled) {
      links.push(
        toLinkPage("Registry Mirror", `/addons/addon-registry-mirror`, {
          names: ["addons.registryMirror"],
          subLevel: 1,
        }),
      );
    }
    links.push(
      toLinkPage("Firewall", "/firewall", { names: ["firewall"], showDirty: true }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Health", "/nodes"),
      toLinkPage("kubeconfig", "/kubeconfig"),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
      toLinkPage("Logs", "/cluster-logs"),
      toLinkExternal("Metrics", "metrics-mk8s", mk8s.alias, mk8s.name),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting || asyncValidating,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting || asyncValidating,
        }),
        toLinkButton(asyncValidating ? "Validating..." : "Update", {
          onMouseDown: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting || asyncValidating,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting || asyncValidating,
        }),
      );
    }

    return links;
  }, [
    anyModified,
    submitting,
    asyncValidating,
    isAddonCplnPlatformEnabled,
    isAddonAWSECREnabled,
    isAddonAWSEFSEnabled,
    isAddonAWSELBEnabled,
    isAddonACREnabled,
    isAddonAWIEnabled,
    isAddonLogsEnabled,
    isAddonMetricsEnabled,
    isAddonNvidiaEnabled,
    isAddonRegistryMirrorEnabled,
    eventlogHook.hasWarning,
  ]);

  return (
    <BasePathContext.Provider value={basePath}>
      <Mk8sDetailHeader cluster={mk8s} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary mk8s={mk8s} />} />
              <Route path={`provider-${provider}/*`} element={<Provider />} />
              <Route path={`node-pools-${provider}/*`} element={<NodePools />} />
              {["hetzner"].includes(provider) ? (
                <Route path={`dedicated-server-node-pools-${provider}/*`} element={<UnmanagedNodePools />} />
              ) : null}
              {["lambdalabs", "oblivus", "paperspace"].includes(provider) ? (
                <Route path={`unmanaged-node-pools-${provider}/*`} element={<UnmanagedNodePools />} />
              ) : null}
              <Route path={`nodes`} element={<Mk8sNodes mk8s={mk8s} />} />
              <Route path={`addons`} element={<Addons />} />
              <Route path={`addons/addon-cpln-platform/*`} element={<AddonByok />} />
              <Route path={`addons/addon-aws-ecr/*`} element={<AddonAwsEcr />} />
              <Route path={`addons/addon-aws-efs/*`} element={<AddonAwsEfs />} />
              <Route path={`addons/addon-aws-elb/*`} element={<AddonAwsElb />} />
              <Route path={`addons/addon-azure-acr/*`} element={<AddonAzureAcr />} />
              <Route path={`addons/addon-azure-workload-identity/*`} element={<AddonAzureWorkloadIdentity />} />
              <Route path={`addons/addon-logs/*`} element={<AddonLogs />} />
              <Route path={`addons/addon-metrics/*`} element={<AddonMetrics />} />
              <Route path={`addons/addon-nvidia/*`} element={<AddonNvidia />} />
              <Route path={`addons/addon-registry-mirror/*`} element={<AddonRegistryMirror />} />
              <Route path={`firewall/*`} element={<Firewall />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route path={`kubeconfig`} element={<KubeConfig mk8s={mk8s} />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"mk8s"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"mk8s"}
                    itemName={mk8s.name}
                    resourceId={mk8s.id}
                    version={mk8s.version}
                    selfLink={linksOf(mk8s).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(mk8s).self!} />} />
              <Route path={`cluster-logs`} element={<Mk8sLogs mk8s={mk8s} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
